import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';
import type { BrandAccessFilter } from './supabase-queries';

/**
 * Get filter counts with respect to other applied filters
 * This creates a cross-filter effect where selecting one filter affects counts in other filters
 */
export async function getDynamicFilterCounts(
  uiFilters: Record<string, string[]>,
  brandAccessFilter?: BrandAccessFilter
) {
  console.log('getDynamicFilterCounts called with uiFilters:', uiFilters, 'and brandAccessFilter:', brandAccessFilter);
  
  const counts: Record<string, Record<string, number>> = {
    brand: {},
    category: {},
    sentiment: {},
    source: {}
  };
  
  try {
    const applyFiltersExcept = (query: any, excludeField: string) => {
      // Apply date range from uiFilters (always applied, regardless of excludeField)
      if (uiFilters.startDate && uiFilters.startDate.length > 0 &&
          uiFilters.endDate && uiFilters.endDate.length > 0) {
        query = query.gte('Date', uiFilters.startDate[0])
                     .lte('Date', uiFilters.endDate[0]);
      }
      
      // Handle Brand Filtering (Primary: brandAccessFilter, Secondary: uiFilters.brandName)
      if (excludeField !== 'brandName') { 
        if (brandAccessFilter?.type === 'specific' && brandAccessFilter.names && brandAccessFilter.names.length > 0) {
          query = query.in('Brand name', brandAccessFilter.names);
          // If UI also has a brand filter, it means we are looking for counts of other facets
          // for items matching specific brands from permissions AND specific brands from UI.
          // So, the UI filter further restricts the brands.
          if (uiFilters.brandName && uiFilters.brandName.length > 0) {
            // Get intersection of brandAccessFilter.names and uiFilters.brandName
            const effectiveBrandNames = brandAccessFilter.names.filter(name => uiFilters.brandName!.includes(name));
            if (effectiveBrandNames.length > 0) {
              query = query.in('Brand name', effectiveBrandNames); // Re-apply with intersection
            } else {
              // If intersection is empty, means no common brands, so add impossible condition
              query = query.eq('Brand name', uuidv4()); 
            }
          }
        } else if (brandAccessFilter?.type === 'all' || !brandAccessFilter) {
          if (uiFilters.brandName && uiFilters.brandName.length > 0) {
            query = query.in('Brand name', uiFilters.brandName);
          }
        } else if (brandAccessFilter?.type === 'none') {
          query = query.eq('Brand name', uuidv4()); // Impossible condition
        }
      }
      
      // Apply category filters
      if (excludeField !== 'category' && uiFilters.category && uiFilters.category.length > 0) {
        query = query.in('Category', uiFilters.category);
      }
      
      // Apply sentiment filters
      if (excludeField !== 'sentiment' && uiFilters.sentiment && uiFilters.sentiment.length > 0) {
        query = query.in('Sentiment', uiFilters.sentiment);
      }
      
      // Apply source filters
      if (excludeField !== 'source' && uiFilters.source && uiFilters.source.length > 0) {
        query = query.in('Source', uiFilters.source);
      }
      
      return query;
    };
    
    // Fetch and count brands
    if (brandAccessFilter?.type !== 'none') { // Only count brands if user has some brand access
      console.log('Fetching brand counts...');
      let brandQueryBase = supabase.from('NewsClipping').select('"Brand name"'); 
      // Apply all other filters when counting brands
      let brandQuery = applyFiltersExcept(brandQueryBase, 'brandName'); 
      const { data: brandData, error: brandError } = await brandQuery;
  
      if (brandError) {
        console.error('[getDynamicFilterCounts] Error fetching brand counts:', brandError);
      } else if (brandData) {
        brandData.forEach((item: any) => {
          const brand = item['Brand name'];
          if (brand) {
            counts.brand[brand] = (counts.brand[brand] || 0) + 1;
          }
        });
      }
    } else {
      console.log('Skipping brand counts due to brandAccessFilter type none.');
      // counts.brand will remain empty
    }
    
    // Fetch and count categories
    console.log('Fetching category counts...');
    let categoryQueryBase = supabase.from('NewsClipping').select('Category').not('Category', 'is', null).neq('Category', '');
    let categoryQuery = applyFiltersExcept(categoryQueryBase, 'category');
    const { data: categoryData, error: categoryError } = await categoryQuery;

    if (categoryError) {
      console.error('[getDynamicFilterCounts] Error fetching category counts:', categoryError);
    } else if (categoryData) {
      categoryData.forEach((item: any) => {
        const category = item.Category;
        if (category) {
          counts.category[category] = (counts.category[category] || 0) + 1;
        }
      });
    }
    
    // Fetch and count sentiments
    console.log('Fetching sentiment counts...');
    let sentimentQueryBase = supabase.from('NewsClipping').select('Sentiment');
    let sentimentQuery = applyFiltersExcept(sentimentQueryBase, 'sentiment');
    const { data: sentimentData, error: sentimentError } = await sentimentQuery;

    if (sentimentError) {
      console.error('[getDynamicFilterCounts] Error fetching sentiment counts:', sentimentError);
    } else if (sentimentData) {
      sentimentData.forEach((item: any) => {
        const sentiment = item.Sentiment;
        if (sentiment) {
          counts.sentiment[sentiment] = (counts.sentiment[sentiment] || 0) + 1;
        }
      });
    }
    
    // Fetch and count sources
    console.log('Fetching source counts...');
    let sourceQueryBase = supabase.from('NewsClipping').select('Source');
    let sourceQuery = applyFiltersExcept(sourceQueryBase, 'source');
    const { data: sourceData, error: sourceError } = await sourceQuery;

    if (sourceError) {
      console.error('[getDynamicFilterCounts] Error fetching source counts:', sourceError);
    } else if (sourceData) {
      sourceData.forEach((item: any) => {
        const source = item.Source;
        if (source) {
          counts.source[source] = (counts.source[source] || 0) + 1;
        }
      });
    }

    console.log('getDynamicFilterCounts completed. Counts:', counts);
    return counts;
  } catch (error) {
    console.error('[getDynamicFilterCounts] Error in getDynamicFilterCounts:', error);
    return counts; // Return empty/partial counts on error
  }
} 